import { ReactElement, useEffect, useState } from 'react'
import { AppSettings, CurrentUser } from 'interfaces/app'
import App from 'components/pages/app/app'
import { CompanySharedTicket, getCompanySharedTickets } from 'lib/api/tickets/tickets'
import { useUserContext } from 'providers/user-provider'
import { toast } from 'lib/components/toast/toast'
import CollaboratorContextProvider from 'providers/collaborator-context-provider'
import CollaboratorHeader from 'lib/components/collaborator-header/collaborator-header'
import { ArrowLeftIcon, ImageIcon, LoaderCircleIcon, MessageSquareIcon } from 'lucide-react'
import Avatar from 'components/core/avatar'
import { TicketStatusBadge } from 'lib/components/badge/badge'
import FeatureFlagsProvider from 'lib/components/feature-flags/feature-flags-provider'

interface CollaboratorTicketsListPageProps {
  settings: AppSettings
  user: CurrentUser
}

function getIdFromUrl(): string {
  const exp = /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/
  const match = window.location.pathname.match(exp)
  return match ? match[0] : ''
}

const CollaboratorTicketsList = () => {
  const [tickets, setTickets] = useState<CompanySharedTicket[]>([])
  const [loading, setLoading] = useState(true)
  const { token } = useUserContext()

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const ticketGuid = getIdFromUrl()
        const response = await getCompanySharedTickets(ticketGuid, token)
        setTickets(response.data || [])
      } catch (error) {
        console.error('Error fetching tickets:', error)
        toast.error('There was a problem loading your tickets. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    fetchTickets()
  }, [token])

  if (loading) {
    return (
      <div className="tw-mx-auto tw-max-w-4xl tw-p-6">
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-py-12 tw-text-center tw-text-gray-500">
          <LoaderCircleIcon className="tw-animate-spin" />
          Loading tickets...
        </div>
      </div>
    )
  }

  return (
    <div className="tw-mx-auto tw-max-w-screen-xl tw-p-6">
      <a
        href={`/share/${getIdFromUrl()}`}
        className="tw-mb-4 tw-flex tw-items-center tw-gap-2 tw-text-cornflower-500 hover:tw-underline"
      >
        <ArrowLeftIcon className="tw-h-4 tw-w-4" />
        Back to request
      </a>
      <div className="tw-mb-6 tw-max-w-3xl">
        <h1 className="tw-mb-2 tw-flex tw-items-center tw-gap-2 tw-text-2xl tw-font-semibold">
          <MessageSquareIcon className="tw-h-6 tw-w-6" />
          All collaboration requests
        </h1>
        <p className="tw-text-gray-500">
          These are the design requests you&apos;ve been invited to review and provide feedback on for this company. To
          access requests for other companies, please log in using a collaboration link provided by that company.
        </p>
      </div>

      {tickets.length > 0 ? (
        <table className="tw-w-full tw-border-collapse tw-bg-white">
          <thead className="tw-border-b-2 tw-border-l-0 tw-border-r-0 tw-border-t tw-border-solid tw-border-neutral-200 tw-bg-neutral-50 tw-text-xs tw-uppercase tw-text-neutral-500">
            <tr>
              <th className="tw-p-4 tw-font-normal">Title</th>
              <th className="tw-p-4 tw-font-normal">Status</th>
              <th className="tw-p-4 tw-font-normal">Invited By</th>
              <th className="tw-p-4 tw-font-normal">Last Updated</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((ticket) => (
              <tr
                key={ticket.id}
                className="tw-border-b-2 tw-border-l-0 tw-border-r-0 tw-border-t-2 tw-border-solid tw-border-neutral-200"
              >
                <td className="tw-p-2 tw-pr-4">
                  <div className="tw-flex tw-items-center tw-gap-4">
                    {ticket?.thumbnail?.url && !ticket?.thumbnail?.placeholder ? (
                      <img
                        src={ticket.thumbnail.url}
                        alt={ticket.subject}
                        className="tw-h-14 tw-w-14 tw-rounded-md tw-object-cover"
                      />
                    ) : (
                      <div className="tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-md tw-bg-neutral-100 tw-text-neutral-400">
                        <ImageIcon className="" />
                      </div>
                    )}
                    <a
                      href={`/share/${ticket.shareId}`}
                      className="tw-font-semibold tw-text-cornflower-500 hover:tw-underline"
                    >
                      {ticket.subject}
                    </a>
                  </div>
                </td>
                <td className="tw-p-4">
                  <TicketStatusBadge status={ticket.friendlyStatusName} className="tw-flex-shrink-0" />
                </td>
                <td className="tw-p-4">
                  <div className="tw-flex tw-items-center">
                    <Avatar name={ticket.invitedByUser.fullName} />
                    <span className="tw-ml-2">{ticket.invitedByUser.fullName}</span>
                  </div>
                </td>
                <td className="tw-p-4">{ticket.lastUpdatedForUser}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="tw-py-12 tw-text-center tw-text-gray-500">
          You don&apos;t have any other shared requests in this company.
        </div>
      )}
    </div>
  )
}

export default function CollaboratorTicketsListPage({
  settings,
  user,
}: CollaboratorTicketsListPageProps): ReactElement {
  return (
    <App settings={settings} user={user} allowCollaborators>
      <CollaboratorContextProvider settings={settings}>
        <FeatureFlagsProvider>
          <CollaboratorHeader />
          <CollaboratorTicketsList />
        </FeatureFlagsProvider>
      </CollaboratorContextProvider>
    </App>
  )
}
